import {
  WebsiteButtonAndFormFieldTypes,
  WebsiteButtonsInterface,
  WebsiteFontSettingsInterface,
  WebsiteFormFieldsSettings,
  WebsiteSettingsState,
  WebsiteSettingsTypographyTypeEnum,
  WebsiteThemeButtonsInterface,
  WebsiteThemeFormFieldsInterface,
} from '../../../shared/types/website-settings-types'

export const getWebsiteName = (state: WebsiteSettingsState) => state.websiteName

export const getWebsiteLayoutPageId = (state: WebsiteSettingsState) =>
  state.websiteLayoutPageId

export const getWebsiteLocale = (state: WebsiteSettingsState) =>
  state.websiteLocale

export const getCurrentWebsiteLocale = (state: WebsiteSettingsState) =>
  state.currentWebsiteLocale

export const getWebsiteLocales = (state: WebsiteSettingsState) =>
  state.websiteLocales

export const getWebsiteStyles = (state: WebsiteSettingsState) =>
  state.websiteStyles

export const getWebsiteStylesTypographyParagraph = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.typography?.paragraph

export const getWebsiteStylesTypographyHeading = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.typography?.heading

export const getWebsiteStylesPrimaryButtons = (state: WebsiteSettingsState) =>
  state.websiteStyles?.buttonsAndLinks?.primary

export const getWebsiteStylesSecondaryButtons = (state: WebsiteSettingsState) =>
  state.websiteStyles?.buttonsAndLinks?.secondary

export const getWebsiteStylesPrimaryButtonsTypography = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.buttonsAndLinks?.primary?.typography
export const getWebsiteStylesSecondaryButtonsTypography = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.buttonsAndLinks?.secondary?.typography

export const getWebsiteStylesFormFields = (state: WebsiteSettingsState) =>
  state.websiteStyles?.formFields

export const getWebsiteStylesFormFieldsTypography = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.formFields?.typography

export const getWebsiteStylesAllTypography = (state: WebsiteSettingsState) => {
  const pickTypography = (typography?: WebsiteFontSettingsInterface) =>
    typography?.fontFamily
      ? {
          fontFamily: typography.fontFamily,
          fontWeight: typography.fontWeight as string,
          fontStyle: typography.fontStyle as string,
        }
      : null

  return [
    pickTypography(state.websiteStyles?.formFields?.typography),
    pickTypography(state.websiteStyles?.buttonsAndLinks?.primary?.typography),
    pickTypography(state.websiteStyles?.buttonsAndLinks?.secondary?.typography),
    pickTypography(state.websiteStyles?.typography?.heading),
    pickTypography(state.websiteStyles?.typography?.paragraph),
  ].filter(typography => typography !== null)
}

export const getWebsiteStylesPrimaryButtonsBorder = (
  state: WebsiteSettingsState,
) => state.websiteStyles?.buttonsAndLinks?.primary?.border

export const getWebsiteThemeBackground = (state: WebsiteSettingsState) =>
  state.websiteDraftTheme?.background ?? state.websiteTheme?.background

export const getWebsiteStylesLayout = (state: WebsiteSettingsState) =>
  state.websiteStyles?.layout

export const getWebsiteThemesList = (state: WebsiteSettingsState) =>
  state.websiteThemesList

export const getWebsiteTheme = (state: WebsiteSettingsState) =>
  state.websiteTheme

export const getCurrentWebsiteTheme = (state: WebsiteSettingsState) =>
  state.websiteDraftTheme || state.websiteTheme

export const getWebsiteDraftTheme = (state: WebsiteSettingsState) =>
  state.websiteDraftTheme

export const buttonAndFormFieldSelectorMap: Record<
  WebsiteButtonAndFormFieldTypes,
  (
    state: WebsiteSettingsState,
  ) => Partial<WebsiteButtonsInterface | WebsiteFormFieldsSettings> | undefined
> = {
  [WebsiteSettingsTypographyTypeEnum.PrimaryButton]:
    getWebsiteStylesPrimaryButtons,
  [WebsiteSettingsTypographyTypeEnum.SecondaryButton]:
    getWebsiteStylesSecondaryButtons,
  [WebsiteSettingsTypographyTypeEnum.FormField]: getWebsiteStylesFormFields,
}

export const typographySelectorMap = {
  [WebsiteSettingsTypographyTypeEnum.Paragraph]:
    getWebsiteStylesTypographyParagraph,
  [WebsiteSettingsTypographyTypeEnum.Heading]:
    getWebsiteStylesTypographyHeading,
  [WebsiteSettingsTypographyTypeEnum.PrimaryButton]:
    getWebsiteStylesPrimaryButtonsTypography,
  [WebsiteSettingsTypographyTypeEnum.SecondaryButton]:
    getWebsiteStylesSecondaryButtonsTypography,
  [WebsiteSettingsTypographyTypeEnum.FormField]:
    getWebsiteStylesFormFieldsTypography,
}

type WebsiteThemeButtonKeys = keyof WebsiteThemeButtonsInterface
export interface ButtonColorKeysMapping {
  textColor: WebsiteThemeButtonKeys
  backgroundColor: WebsiteThemeButtonKeys
  hoveredBackgroundColor: WebsiteThemeButtonKeys
  pressedBackgroundColor: WebsiteThemeButtonKeys
  borderColor: WebsiteThemeButtonKeys
}

type WebsiteThemeFormFieldsKeys = keyof WebsiteThemeFormFieldsInterface

export interface FormFieldsColorKeysMapping {
  textColor: WebsiteThemeFormFieldsKeys
  backgroundColor: WebsiteThemeFormFieldsKeys
  hoveredBackgroundColor: ''
  pressedBackgroundColor: ''
  borderColor: WebsiteThemeFormFieldsKeys
}

export const buttonAndFormFieldColorKeyMap: Record<
  WebsiteButtonAndFormFieldTypes,
  ButtonColorKeysMapping | FormFieldsColorKeysMapping
> = {
  [WebsiteSettingsTypographyTypeEnum.PrimaryButton]: {
    textColor: 'primaryButtonTextColor',
    backgroundColor: 'primaryButtonBackgroundColor',
    hoveredBackgroundColor: 'primaryHoveredButtonBackgroundColor',
    pressedBackgroundColor: 'primaryPressedButtonBackgroundColor',
    borderColor: 'primaryButtonBorderColor',
  },
  [WebsiteSettingsTypographyTypeEnum.SecondaryButton]: {
    textColor: 'secondaryButtonTextColor',
    backgroundColor: 'secondaryButtonBackgroundColor',
    hoveredBackgroundColor: 'secondaryHoveredButtonBackgroundColor',
    pressedBackgroundColor: 'secondaryPressedButtonBackgroundColor',
    borderColor: 'secondaryButtonBorderColor',
  },
  [WebsiteSettingsTypographyTypeEnum.FormField]: {
    textColor: 'formFieldTextColor',
    backgroundColor: 'formFieldBackgroundColor',
    hoveredBackgroundColor: '',
    pressedBackgroundColor: '',
    borderColor: 'formFieldBorderColor',
  },
  // [WebsiteSettingsTypographyTypeEnum.GhostButton]: {
  //   buttonTextColor: 'ghostButtonTextColor',
  //   buttonBackgroundColor: 'ghostHoveredButtonBackgroundColor',
  //   hoveredButtonBackgroundColor: 'ghostHoveredButtonBackgroundColor',
  //   pressedButtonBackgroundColor: 'ghostPressedButtonBackgroundColor',
  // },
}

export const getWebsiteId = (state: WebsiteSettingsState) => state.websiteId

export const getWebsiteDomain = (state: WebsiteSettingsState) =>
  state.websiteDomain

export const getWebsiteActivate = (state: WebsiteSettingsState) =>
  state.websiteActive

export const websiteSettingsSelectors = {
  getWebsiteId,
  getWebsiteDomain,
  getWebsiteActivate,
  getWebsiteLocale,
}
